/**
 * 前端 Sentry 操作封装。
 * 页面和组件通过这里统一上报异常、消息和用户信息，避免到处直接依赖 Sentry API。
 */
import * as Sentry from "@sentry/vue";

type CaptureContext = {
  tags?: Record<string, string | number | boolean | undefined>;
  extra?: Record<string, unknown>;
};

export function useSentryClient() {
  /**
   * 捕获异常，并附加额外的上下文信息
   * @param error
   */
  const captureException = (error: unknown, context: CaptureContext = {}) => {
    Sentry.captureException(error, {
      tags: normalizeTags(context.tags),
      extra: context.extra,
    });
  };

  /**
   * 捕获消息，并附加额外的上下文信息
   * @param message
   * @param context
   */
  const captureMessage = (
    message: string,
    context: CaptureContext & { level?: Sentry.SeverityLevel } = {},
  ) => {
    Sentry.captureMessage(message, {
      level: context.level || "info",
      tags: normalizeTags(context.tags),
      extra: context.extra,
    });
  };

  /**
   * 设置用户信息
   */
  const setUser = (
    user: { id?: string; email?: string; username?: string } | null,
  ) => {
    Sentry.setUser(user);
  };

  return {
    captureException,
    captureMessage,
    setUser,
  };
}

/**
 * 将 tags 中的 undefined 值过滤掉，并确保所有值都转换为字符串。
 */
function normalizeTags(
  tags?: Record<string, string | number | boolean | undefined>,
) {
  if (!tags) {
    return undefined;
  }

  return Object.fromEntries(
    Object.entries(tags)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => [key, String(value)]),
  );
}
